import { useIsFetching, useIsMutating, useQuery, useQueryClient, } from '@tanstack/react-query';
import { FetchApiError } from '@zurich-framework/core/errors';
import { devError } from '../../utils';
import { useFetchApi, } from './useFetchApi';
const TIMEOUT_WARNING_MS = 10000; // 10 seconds
export function getDataState(status, fetchStatus, isRefetching) {
    if (isRefetching) {
        return 'refetching';
    }
    switch (status) {
        case 'loading':
            return fetchStatus === 'idle' ? 'unset' : 'loading';
        case 'success':
            return 'success';
        case 'error':
            return 'error';
    }
}
export const useApiIsMutating = useIsMutating;
export const useApiIsFetching = useIsFetching;
export const useApiQueryClient = useQueryClient;
/**
 * React Query hook for fetching data from the API.
 *
 * Use this hook if you want to query data from the API to display it in the UI.
 *
 * Prefer `useApi` if:
 * - you want to send data to the API (create, update, delete)
 * - you need to fetch data inside an event callback like onClick and you need success or error handling inside the same closure
 */
export function useApiQuery({ request, params, options: { timeoutWarningMs = TIMEOUT_WARNING_MS, handleError, errorText, ...otherOptions }, queryKey, queryOptions = {}, }) {
    const { fetchApi, showApiError } = useFetchApi();
    const { status, fetchStatus, data, refetch, isRefetching } = useQuery(
    // queryKey
    queryKey ?? [request.route, params], 
    // queryFn
    async ({ signal }) => {
        const res = await fetchApi(request, params, {
            ...otherOptions,
            throwOnError: true,
            timeoutWarningMs,
            errorText,
            signal,
        });
        // TODO: This is to handle 204 as "undefined" is not a valid response for useQuery (https://tanstack.com/query/v4/docs/react/guides/migrating-to-react-query-4#undefined-is-an-illegal-cache-value-for-successful-queries)
        // This is not typesafe!
        if (res === undefined) {
            return null;
        }
        // this should never happen, because we always throw on errors
        // if a code change results in an errornous response here, we throw again with an unhandled error
        if (res === null) {
            throw new Error('Unhandled fetchApi error in query hook');
        }
        return res;
    }, 
    // options
    {
        // TODO: onError is deprecated, will be removed in next major version of React Query
        onError: (error) => {
            if (error instanceof FetchApiError) {
                if (handleError) {
                    handleError(error, (props) => {
                        showApiError({
                            text: errorText,
                            retryCallback: async () => {
                                await refetch();
                            },
                            ...props,
                        });
                    });
                }
                else {
                    showApiError({
                        text: errorText,
                        retryCallback: async () => {
                            await refetch();
                        },
                        error,
                    });
                }
            }
            else {
                // this should never happen, because fetchApi will only throw FetchApiError
                devError('Unknown React Query error');
            }
        },
        ...queryOptions,
    });
    return {
        data,
        dataState: getDataState(status, fetchStatus, isRefetching),
        refetch,
    };
}
